import { type UserAccessResponse, type UserLevel } from '@common/types/api';
import { type GUID } from '@common/types/brandedTypes';
import type User from '@common/types/models/User';
import { getFullName } from '@common/types/models/User';

interface AdditionalUser {
  readonly id: GUID;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email: string;
  readonly fullName: (startFromLastName?: boolean) => string | undefined;
  readonly userLevel: UserLevel;
  readonly isDeviceOwner: boolean;
  readonly deviceAccessLevel: UserLevel;
}

export const isAdditionalUser = (
  v: unknown,
): v is AdditionalUser =>
  typeof v === 'object' && v != null && Object.keys(v).includes('deviceAccessLevel');

export const createAdditionalUserFromResponse = (apiUser: UserAccessResponse): AdditionalUser => ({
  id: apiUser.user.id,
  firstName: apiUser.user.firstName,
  lastName: apiUser.user.lastName,
  email: apiUser.user.email,
  fullName: getFullName,
  userLevel: apiUser.user.levelId,
  isDeviceOwner: apiUser.isOwner,
  deviceAccessLevel: apiUser.levelId,
});

const compareName = (
  a: string | undefined,
  b: string | undefined,
) => {
  if (a === b) {
    return 0;
  }
  if (a != null && b != null) {
    return a.localeCompare(b);
  }
  return a == null ? -1 : 1;
};

export const compareFullName = (
  a: AdditionalUser | User,
  b: AdditionalUser | User,
) => {
  const result = compareName(a.lastName, b.lastName);
  return result === 0 ? compareName(a.firstName, b.firstName) : result;
};

export default AdditionalUser;
